import React, { useEffect, useState, useMemo, useCallback } from "react";
import CustomTabs from "../../../components/Tabs/CustomTabs";
import Header from "../../../components/Header/Header";
import SearchableTable from "../../../components/Tables/SearchableTable";
import { useDispatch, useSelector } from "react-redux";
import CustomerSelector from "../../../components/Selector/CustomerSelector";
import { fetchCustomerLedger } from "../../../store/Slices/CustomerLedgerSlice";
import CompanySelector from "../../../components/Selector/CompanySelector";
import { fetchSupplierLedger } from "../../../store/Slices/SupplierLedgerSlice";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import { CustomerCompleteLedgerColumns } from "../../../assets/Columns/CustomerCompleteLedgerColumns";
import { CompanyCompleteLedgerColumns } from "../../../assets/Columns/CompanyCompleteLedgerColumns";
import PrintButton from "../../../components/Buttons/PrintButton";
import ExportToExcelButton from "../../../utils/ExportToExcel";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchCustomerItemSummary } from "../../../store/Slices/CustomerItemSummarySlice";
import { CustomerItemSummaryColumns } from "../../../assets/Columns/CustomerItemSummaryColumns";
import ItemSummaryTable from "../../../components/Tables/ItemSummaryTable";
import FigureCard from "../../../components/Cards/FigureCard";
import { fetchCompanyItemSummary } from "../../../store/Slices/CompanyItemSummarySlice";
import { filterTableRowsBySearch } from "../../../utils/tableSearch";
import { getAppBasePath, isSuperAdminRole } from "../../../utils/appPaths";
import LedgerEntryCards from "../../../components/Ledger/LedgerEntryCards";
import LedgerFilterTabs from "../../../components/Ledger/LedgerFilterTabs";
import { BsSearch } from "react-icons/bs";
import { usePageRefresh } from "../../../hooks/usePageRefresh";

const CUSTOMER_LEDGER_TABS = ["All", "SH", "Item", "Cash", "Return"];
const CUSTOMER_LEDGER_LABELS = { SH: "Customer" };
const COMPANY_LEDGER_TABS = ["All", "Item", "Cash"];
const SUMMARY_TABS = ["All", "SH"];


const isOpeningLedgerRow = (row) =>
  Boolean(row?._isOpening) ||
  String(row?.name || "").toLowerCase() === "opening balance" ||
  String(row?.desc || "").toLowerCase() === "opening balance";

const withOpeningBalanceTop = (rows = [], openingAmt = 0, sortDir = "desc") => {
  const amt = Number(openingAmt) || 0;
  const openingRow = {
    invoice: "-",
    name: "Opening Balance",
    desc: "Opening Balance",
    date: 0,
    n_date: "-",
    qty: 0,
    price: 0,
    discount: 0,
    unit: "",
    cr: amt >= 0 ? amt : 0,
    dr: amt < 0 ? Math.abs(amt) : 0,
    _isOpening: true,
  };
  const rest = (rows || []).filter((r) => !isOpeningLedgerRow(r));
  const indexed = [openingRow, ...rest].map((row, index) => ({
    ...row,
    __stable: index,
  }));
  // Keep opening first visually; recompute balance chronologically from opening
  let balance = 0;
  const chrono = [...indexed].sort((a, b) => {
    if (a._isOpening && !b._isOpening) return -1;
    if (!a._isOpening && b._isOpening) return 1;
    if (a._isOpening && b._isOpening) return 0;
    const dateDiff = Number(a.date || 0) - Number(b.date || 0);
    if (dateDiff !== 0) return dateDiff;
    return a.__stable - b.__stable;
  });
  const withBal = chrono.map(({ __stable, ...row }) => {
    balance += Number(row.cr || 0);
    balance -= Number(row.dr || 0);
    return { ...row, balance };
  });
  const opening = withBal.find((r) => r._isOpening);
  const others = withBal.filter((r) => !r._isOpening);
  const orderedOthers =
    sortDir === "asc" ? others : [...others].reverse();
  return opening ? [opening, ...orderedOthers] : orderedOthers;
};

const Ledger = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location.state?.company ? 1 : 0
  );
  const [SearchText, setSearchText] = useState("");
  const [SelectedCustomer, setSelectedCustomer] = useState(
    () => location.state?.customer || ""
  );
  const [SelectedSupplier, setSelectedSupplier] = useState(
    () => location.state?.company || ""
  );
  const [CurrentTab, setCurrentTab] = useState("All");
  const [CurrentSummaryTab, setCurrentSummaryTab] = useState("All");
  const [ledgerSort, setLedgerSort] = useState("desc");

  const dispatch = useDispatch();
  const CustomerLedgers = useSelector((state) => state.CustomerLedgers);
  const SupplierLedgers = useSelector((state) => state.SupplierLedgers);
  const CustomerItemSummaryState = useSelector(
    (state) => state.CustomerItemSummaryState
  );
  const CompanyItemSummaryState = useSelector(
    (state) => state.CompanyItemSummaryState
  );

  const totalReturn = useMemo(() => {
    if (!Array.isArray(CustomerLedgers.data)) return 0;
    return CustomerLedgers.data
      .filter((dt) => dt.type === 3)
      .reduce((acc, item) => acc + (item.cr || 0), 0);
  }, [CustomerLedgers.data]);

  const customerLedgerSummary = useMemo(() => {
    if (!CustomerLedgers.data.length) return { cr: 0, dr: 0, qty: 0 };

    return CustomerLedgers.data.reduce(
      (acc, item) => ({
        cr: acc.cr + (item.cr || 0),
        dr: acc.dr + (item.dr || 0),
        qty: acc.qty + (item.qty || 0),
      }),
      { cr: 0, dr: 0, qty: 0 }
    );
  }, [CustomerLedgers.data]);

  const supplierLedgerSummary = useMemo(() => {
    if (!SupplierLedgers.data.length) return { cr: 0, dr: 0, qty: 0 };

    return SupplierLedgers.data.reduce(
      (acc, item) => ({
        cr: acc.cr + (item.cr || 0),
        dr: acc.dr + (item.dr || 0),
        qty: acc.qty + (item.qty || 0),
      }),
      { cr: 0, dr: 0, qty: 0 }
    );
  }, [SupplierLedgers.data]);

  useEffect(() => {
    if (SelectedCustomer._id) {
      dispatch(
        fetchCustomerLedger({ id: SelectedCustomer._id, sort: ledgerSort })
      );
      dispatch(fetchCustomerItemSummary(SelectedCustomer._id));
    }
  }, [SelectedCustomer, ledgerSort]);

  useEffect(() => {
    if (SelectedSupplier._id) {
      dispatch(fetchSupplierLedger(SelectedSupplier._id));
      dispatch(fetchCompanyItemSummary(SelectedSupplier._id));
    }
  }, [SelectedSupplier]);

  const refreshLedger = useCallback(() => {
    if (SelectedCustomer._id) {
      dispatch(
        fetchCustomerLedger({ id: SelectedCustomer._id, sort: ledgerSort })
      );
      dispatch(fetchCustomerItemSummary(SelectedCustomer._id));
    }
    if (SelectedSupplier._id) {
      dispatch(fetchSupplierLedger(SelectedSupplier._id));
      dispatch(fetchCompanyItemSummary(SelectedSupplier._id));
    }
  }, [
    SelectedCustomer._id,
    SelectedSupplier._id,
    ledgerSort,
    dispatch,
  ]);

  usePageRefresh(refreshLedger, [SelectedCustomer._id, SelectedSupplier._id]);

  useEffect(() => {
    setCurrentTab("All");
  }, [activeTab]);

  useEffect(() => {
    const customer = location.state?.customer;
    const company = location.state?.company;
    if (customer) {
      setActiveTab(0);
      setSelectedCustomer(customer);
    } else if (company) {
      setActiveTab(1);
      setSelectedSupplier(company);
    }
  }, [location.state]);

  const isSuperAdmin = isSuperAdminRole();
  const basePath = getAppBasePath();

  const companyItemSummaryRows = useMemo(() => {
    const d = CompanyItemSummaryState?.data;
    return Array.isArray(d) ? d : [];
  }, [CompanyItemSummaryState.data]);

  const companyExportRows = useMemo(
    () =>
      companyItemSummaryRows.filter((row) => row?.name !== "Total"),
    [companyItemSummaryRows]
  );

  const customerLedgerColumns = useMemo(() => {
    if (CurrentTab === "All" || CurrentTab === "SH") {
      return CustomerCompleteLedgerColumns;
    }
    if (CurrentTab === "Item") {
      return CustomerCompleteLedgerColumns.filter(
        (cl) => cl.id !== "dr" && cl.id !== "desc"
      );
    }
    if (CurrentTab === "Return") {
      return CustomerCompleteLedgerColumns.filter(
        (cl) => cl.id !== "cr" && cl.id !== "desc"
      );
    }
    return CustomerCompleteLedgerColumns.filter(
      (cl) =>
        cl.id !== "cr" && cl.id !== "qty" && cl.id !== "price" && cl.id !== "name"
    );
  }, [CurrentTab]);

  const customerLedgerRows = useMemo(() => {
    const d = CustomerLedgers?.data;
    let rows = [];
    if (CurrentTab === "SH") rows = d?.ShAll || [];
    else if (CurrentTab === "All") rows = d?.All || [];
    else if (CurrentTab === "Item") rows = d?.Item || [];
    else if (CurrentTab === "Cash") rows = d?.Cash || [];
    else if (CurrentTab === "Return") rows = d?.Return || [];
    if (
      SelectedCustomer?._id &&
      (CurrentTab === "All" || CurrentTab === "SH")
    ) {
      return withOpeningBalanceTop(
        rows,
        SelectedCustomer.opening_balance,
        ledgerSort
      );
    }
    return rows;
  }, [
    CurrentTab,
    CustomerLedgers?.data,
    SelectedCustomer?._id,
    SelectedCustomer?.opening_balance,
    ledgerSort,
  ]);

  const customerLedgerExport = useMemo(
    () =>
      filterTableRowsBySearch(
        customerLedgerRows,
        SearchText,
        customerLedgerColumns
      ),
    [customerLedgerRows, SearchText, customerLedgerColumns]
  );

  const companyLedgerColumns = useMemo(() => {
    if (CurrentTab === "All") return CompanyCompleteLedgerColumns;
    if (CurrentTab === "Item") {
      return CompanyCompleteLedgerColumns.filter(
        (dt) => dt.id !== "desc" && dt.id !== "dr"
      );
    }
    return CompanyCompleteLedgerColumns.filter(
      (dt) =>
        dt.id !== "name" && dt.id !== "qty" && dt.id !== "price" && dt.id !== "cr"
    );
  }, [CurrentTab]);

  const companyLedgerRows = useMemo(() => {
    const d = SupplierLedgers?.data;
    if (CurrentTab === "All") return d?.all ?? [];
    if (CurrentTab === "Item") return d?.item ?? [];
    return d?.cash ?? [];
  }, [CurrentTab, SupplierLedgers?.data]);

  const companyLedgerExport = useMemo(
    () =>
      filterTableRowsBySearch(
        companyLedgerRows,
        SearchText,
        companyLedgerColumns
      ),
    [companyLedgerRows, SearchText, companyLedgerColumns]
  );

  return (
    <div className="w-full flex flex-col pt-2 pb-8 font-quicksand">
      <Header title="Ledgers" desc="Overview of your ledger management" />
      <div className="scrollable pb-6">
        <CustomTabs
          tabs={[{ label: "Customer" }, { label: "Company" }]}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          scrollable={isSuperAdmin}
        />
        <div className="w-full mt-2 flex justify-center items-start">
          {activeTab === 0 && (
            <div className="flex flex-col w-full gap-y-5">
              <CustomerSelector
                SelectedCustomer={SelectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
              />
              {SelectedCustomer && (
                <LedgerFilterTabs
                  tabs={CUSTOMER_LEDGER_TABS}
                  labelMap={CUSTOMER_LEDGER_LABELS}
                  active={CurrentTab}
                  onChange={setCurrentTab}
                />
              )}
              {SelectedCustomer && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div
                    className="inline-flex items-center rounded-lg border border-line bg-card p-0.5 shadow-soft"
                    role="group"
                    aria-label="Sort ledger by date"
                  >
                    <button
                      type="button"
                      onClick={() => setLedgerSort("desc")}
                      className={`px-3 h-8 rounded-md text-xs font-bold transition-colors ${
                        ledgerSort === "desc"
                          ? "bg-main text-white"
                          : "text-inkSecondary hover:bg-subtle"
                      }`}
                    >
                      Newest first
                    </button>
                    <button
                      type="button"
                      onClick={() => setLedgerSort("asc")}
                      className={`px-3 h-8 rounded-md text-xs font-bold transition-colors ${
                        ledgerSort === "asc"
                          ? "bg-main text-white"
                          : "text-inkSecondary hover:bg-subtle"
                      }`}
                    >
                      Oldest first
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <PrintButton
                      onClick={() => {
                        navigate(
                          `${basePath}/customer-ledger-report/` +
                            SelectedCustomer._id
                        );
                      }}
                      title="Customers Report"
                    />
                    <ExportToExcelButton
                      data={customerLedgerExport}
                      fileName={"Customer Ledger"}
                    />
                  </div>
                </div>
              )}
                            {SelectedCustomer?._id && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <FigureCard
                    title={"Opening Balance"}
                    value={`Rs ${Number(
                      SelectedCustomer.opening_balance || 0
                    ).toLocaleString("en-PK")}`}
                    textColor={"text-[green]"}
                  />
                  <FigureCard
                    title={"Direct Discount"}
                    value={`Rs ${Number(
                      SelectedCustomer.discount || 0
                    ).toLocaleString("en-PK")}`}
                    textColor={"text-[red]"}
                  />
                </div>
              )}

              {CustomerLedgers.loading ? (
                <div className="flex flex-1 justify-center items-center">
                  <FetchingLoading />
                </div>
              ) : (
                CustomerLedgers.data.length !== 0 &&
                SelectedCustomer && (
                  <>
                    {isSuperAdmin ? (
                      <>
                        <div className="max850:block hidden space-y-3">
                          <div className="relative">
                            <BsSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                            <input
                              type="text"
                              value={SearchText}
                              onChange={(e) => setSearchText(e.target.value)}
                              placeholder="Search ledger..."
                              className="w-full pl-10 pr-4 py-3 rounded-xl border border-line bg-card text-ink text-sm shadow-soft focus:outline-none focus:border-main focus:ring-2 focus:ring-main/15"
                            />
                          </div>
                          <LedgerEntryCards rows={customerLedgerExport} />
                        </div>
                        <div className="max850:hidden">
                          <SearchableTable
                            SearchText={SearchText}
                            setSearchText={setSearchText}
                            SearchPlaceholder={"Search ledger..."}
                            isLedger={true}
                            CurrentData={customerLedgerRows}
                            Columns={customerLedgerColumns}
                          />
                        </div>
                      </>
                    ) : (
                      <SearchableTable
                        SearchText={SearchText}
                        setSearchText={setSearchText}
                        SearchPlaceholder={"Search ledger..."}
                        isLedger={true}
                        CurrentData={customerLedgerRows}
                        Columns={customerLedgerColumns}
                      />
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 my-4">
                      <FigureCard
                        title={"Total"}
                        value={`Rs ${Number(
                          SelectedCustomer.total
                        ).toLocaleString("en-PK")}`}
                      />
                      <FigureCard
                        title={"Return"}
                        value={`Rs ${Number(
                          SelectedCustomer.return_amount
                        ).toLocaleString("en-PK")}`}
                        textColor={"text-[red]"}
                      />
                      <FigureCard
                        title={"Direct Discount"}
                        value={`Rs ${Number(
                          SelectedCustomer.discount
                        ).toLocaleString("en-PK")}`}
                        textColor={"text-[red]"}
                      />
                      <FigureCard
                        title={"Opening Balance"}
                        value={`Rs ${Number(
                          SelectedCustomer.opening_balance
                        ).toLocaleString("en-PK")}`}
                        textColor={"text-[green]"}
                      />
                      <FigureCard
                        title={"Paid"}
                        value={`Rs ${Number(
                          SelectedCustomer.recieved
                        ).toLocaleString("en-PK")}`}
                        textColor={"text-[green]"}
                      />
                      <FigureCard
                        title={"Remaining"}
                        value={`Rs ${Number(
                          SelectedCustomer.recieveable
                        ).toLocaleString("en-PK")}`}
                        textColor={"text-[red]"}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <PrintButton
                        onClick={() => {
                          navigate(
                            `${basePath}/customer-item-summary/` +
                              SelectedCustomer._id
                          );
                        }}
                        title="Customers Report"
                      />
                      <ExportToExcelButton
                        data={CustomerItemSummaryState.data.data}
                        fileName={"Item Summary"}
                      />
                    </div>
                    <div className="w-full mb-10">
                      <LedgerFilterTabs
                        tabs={SUMMARY_TABS}
                        active={CurrentSummaryTab}
                        onChange={setCurrentSummaryTab}
                      />
                      <div className="bg-main text-white text-center font-bold py-4 border-2 rounded-t-lg border-sec2 border-b-0 text-xl uppercase">
                        Item Summary
                      </div>
                      <ItemSummaryTable
                        AllRows={
                          CustomerItemSummaryState?.data?.data &&
                          CurrentSummaryTab === "All"
                            ? CustomerItemSummaryState?.data?.data
                            : CustomerItemSummaryState?.data?.data &&
                              CurrentSummaryTab === "SH"
                            ? CustomerItemSummaryState?.data?.shdata
                            : [{}]
                        }
                        Columns={CustomerItemSummaryColumns}
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 my-4">
                        <FigureCard
                          title={"Box Qty"}
                          value={
                            CustomerItemSummaryState?.data?.data
                              ? `${Number(
                                  CustomerItemSummaryState?.data?.data
                                    .filter((dt) => dt.type === "Box")
                                    .reduce((acc, dt) => {
                                      console.log(dt.name);
                                      if (dt.name !== "SHOPPING IRSHAD")
                                        return acc + dt.qty;
                                      else return acc + 1;
                                    }, 0)
                                ).toLocaleString("en-PK")}`
                              : 0
                          }
                        />
                        <FigureCard
                          title={"SHOPPING Qty"}
                          value={
                            CustomerItemSummaryState?.data?.data
                              ? `${Number(
                                  CustomerItemSummaryState?.data?.data
                                    .filter((dt) => dt.type === "SH")
                                    .reduce((acc, dt) => {
                                      console.log(dt.name);
                                      if (dt.name !== "SHOPPING IRSHAD")
                                        return acc + dt.qty;
                                      else return acc + 1;
                                    }, 0)
                                ).toLocaleString("en-PK")}`
                              : 0
                          }
                          textColor={"text-[red]"}
                        />
                      </div>
                    </div>
                  </>
                )
              )}
            </div>
          )}
          {activeTab === 1 && (
            <div className="flex flex-col w-full gap-y-5">
              <CompanySelector
                SelectedSupplier={SelectedSupplier}
                setSelectedSupplier={setSelectedSupplier}
              />
              {SelectedSupplier && (
                <div className="flex flex-wrap justify-end gap-2">
                  <PrintButton
                    onClick={() => {
                      navigate(
                        `${basePath}/company-ledger-report/` +
                          SelectedSupplier._id
                      );
                    }}
                    title="Company Report"
                  />
                  <ExportToExcelButton
                    data={companyLedgerExport}
                    fileName={"Company Ledger"}
                  />
                </div>
              )}
              {SupplierLedgers.loading ? (
                <div className="flex flex-1 justify-center items-center">
                  <FetchingLoading />
                </div>
              ) : (
                SupplierLedgers.data.length !== 0 &&
                SelectedSupplier && (
                  <>
                    <LedgerFilterTabs
                      tabs={COMPANY_LEDGER_TABS}
                      active={CurrentTab}
                      onChange={setCurrentTab}
                      variant="dark"
                    />
                    {isSuperAdmin ? (
                      <>
                        <div className="max850:block hidden space-y-3">
                          <div className="relative">
                            <BsSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                            <input
                              type="text"
                              value={SearchText}
                              onChange={(e) => setSearchText(e.target.value)}
                              placeholder="Search ledger..."
                              className="w-full pl-10 pr-4 py-3 rounded-xl border border-line bg-card text-ink text-sm shadow-soft focus:outline-none focus:border-main focus:ring-2 focus:ring-main/15"
                            />
                          </div>
                          <LedgerEntryCards rows={companyLedgerExport} />
                        </div>
                        <div className="max850:hidden">
                          <SearchableTable
                            isLedger={true}
                            SearchText={SearchText}
                            setSearchText={setSearchText}
                            SearchPlaceholder={"Search ledger..."}
                            CurrentData={companyLedgerRows}
                            Columns={companyLedgerColumns}
                          />
                        </div>
                      </>
                    ) : (
                      <SearchableTable
                        isLedger={true}
                        SearchText={SearchText}
                        setSearchText={setSearchText}
                        SearchPlaceholder={"Search ledger..."}
                        CurrentData={companyLedgerRows}
                        Columns={companyLedgerColumns}
                      />
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 my-4">
                      <FigureCard
                        title={"Total"}
                        value={`Rs ${Number(
                          SelectedSupplier.total
                        ).toLocaleString("en-PK")}`}
                      />
                      <FigureCard
                        title={"Paid"}
                        value={`Rs ${Number(
                          SelectedSupplier.paid
                        ).toLocaleString("en-PK")}`}
                        textColor={"text-[green]"}
                      />
                      <FigureCard
                        title={"Remaining"}
                        value={`Rs ${Number(
                          SelectedSupplier.payable
                        ).toLocaleString("en-PK")}`}
                        textColor={"text-[red]"}
                      />
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <PrintButton
                        onClick={() => {
                          navigate(
                            `${basePath}/company-item-summary/` +
                              SelectedSupplier._id
                          );
                        }}
                        title="Company item summary"
                      />
                      <ExportToExcelButton
                        data={companyExportRows}
                        fileName={"Company Item Summary"}
                      />
                    </div>

                    <div className="w-full mb-10">
                      <div className="bg-main text-white text-center font-bold py-4 border-2 rounded-t-lg border-sec2 border-b-0 text-xl uppercase">
                        Item Summary
                      </div>
                      {CompanyItemSummaryState.loading ? (
                        <div className="flex justify-center py-8">
                          <FetchingLoading />
                        </div>
                      ) : (
                        <ItemSummaryTable
                          AllRows={companyItemSummaryRows}
                          Columns={CustomerItemSummaryColumns}
                        />
                      )}
                    </div>
                  </>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Ledger;
